import React, { useMemo, useState, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import { brl, fmtDate } from "./_shared/utils";
import { getCategories, catById } from "./_shared/categories";
import { useAppData } from "./_shared/store";
import {
  Download, FileSpreadsheet, FileText, X,
  TrendingUp, TrendingDown, Wallet,
  PiggyBank, Calculator, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const MES_NOMES  = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const MES_CURTOS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

const sumValor = (arr) => arr.reduce((s, x) => s + Number(x.valor || 0), 0);

const getPctColor = (pct) => {
  if (!pct || pct <= 0) return "text-slate-500";
  if (pct < 70)  return "text-emerald-400";
  if (pct < 90)  return "text-yellow-400";
  return "text-rose-400";
};

/* ── Export dropdown ────────────────────────────────────── */
function ExportMenu({ onXLS, onPDF }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="h-10 px-4 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:text-white hover:border-slate-600 transition text-sm font-semibold inline-flex items-center gap-2"
      >
        <Download className="h-4 w-4" /> Exportar Relatório
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-48 bg-slate-800 border border-slate-700 rounded-xl overflow-hidden z-20 shadow-2xl">
            <button
              onClick={() => { onXLS(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-slate-200 hover:bg-slate-700 transition"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-400" /> Excel (.xlsx)
            </button>
            <button
              onClick={() => { onPDF(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-slate-200 hover:bg-slate-700 transition border-t border-slate-700"
            >
              <FileText className="h-4 w-4 text-rose-400" /> PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Month detail modal ─────────────────────────────────── */
function MonthDetailModal({ ymPrefix, data, onClose }) {
  const allCats   = getCategories(data.customCategories);
  const mesIdx    = parseInt(ymPrefix.split("-")[1], 10) - 1;
  const year      = ymPrefix.split("-")[0];
  const mesNome   = `${MES_NOMES[mesIdx]} de ${year}`;

  const entradas  = (data.entradas || []).filter((e) => (e.data || "").startsWith(ymPrefix));
  const fixos     = (data.gastosFixos || []).filter((i) => (i.data || "").startsWith(ymPrefix));
  const variaveis = (data.gastosVariaveis || []).filter((i) => (i.data || "").startsWith(ymPrefix));
  const cartaoTx  = (data.cartoes || []).flatMap((c) =>
    c.transacoes
      .filter((t) => (t.data || "").startsWith(ymPrefix))
      .map((t) => ({ ...t, _cartao: c.nome }))
  );

  const totalEntradas = sumValor(entradas);
  const totalGastos   = sumValor([...fixos, ...variaveis, ...cartaoTx]);
  const diff          = totalEntradas - totalGastos;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-screen flex items-start justify-center p-4 pt-10">
        <div
          className="w-full max-w-xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl mb-10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
            <div>
              <div className="font-display font-bold text-base">{mesNome}</div>
              <div className="text-xs text-slate-400 mt-0.5">Detalhamento completo</div>
            </div>
            <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Summary row */}
          <div className="grid grid-cols-3 divide-x divide-slate-800 border-b border-slate-800">
            <div className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Entradas</div>
              <div className="text-sm font-bold text-blue-300 tabular-nums mt-0.5">{brl(totalEntradas)}</div>
            </div>
            <div className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Gastos</div>
              <div className="text-sm font-bold text-rose-300 tabular-nums mt-0.5">{brl(totalGastos)}</div>
            </div>
            <div className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Diferença</div>
              <div className={`text-sm font-bold tabular-nums mt-0.5 ${diff >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {brl(diff)}
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="max-h-[65vh] overflow-y-auto p-4 space-y-4">
            {entradas.length > 0  && <DetailSection title="Entradas"         total={totalEntradas}    color="text-blue-400"    items={entradas}  allCats={allCats} isIncome />}
            {fixos.length > 0     && <DetailSection title="Gastos Fixos"     total={sumValor(fixos)}  color="text-amber-400"   items={fixos}     allCats={allCats} />}
            {variaveis.length > 0 && <DetailSection title="Gastos Variáveis" total={sumValor(variaveis)} color="text-emerald-400" items={variaveis} allCats={allCats} />}
            {cartaoTx.length > 0  && <DetailSection title="Cartões"          total={sumValor(cartaoTx)} color="text-violet-400" items={cartaoTx}  allCats={allCats} isCartao />}
            {entradas.length === 0 && fixos.length === 0 && variaveis.length === 0 && cartaoTx.length === 0 && (
              <div className="py-10 text-center text-slate-400 text-sm">Nenhum lançamento neste mês.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, total, color, items, allCats, isIncome = false, isCartao = false }) {
  return (
    <div>
      <div className={`flex items-center justify-between mb-2 text-[11px] font-bold uppercase tracking-wider ${color}`}>
        <span>{title} <span className="font-normal opacity-70">({items.length})</span></span>
        <span className="tabular-nums">{brl(total)}</span>
      </div>
      <div className="rounded-xl bg-slate-800/50 overflow-hidden divide-y divide-slate-800/60">
        {items.map((it) => {
          const cat = !isIncome ? catById(it.cat, allCats) : null;
          return (
            <div key={it.id} className="flex items-center justify-between px-3 py-2.5 gap-3 hover:bg-slate-800/40 transition">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{it.desc}</div>
                <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {cat && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: cat.color }} />
                      {cat.name}
                    </span>
                  )}
                  {isCartao && it._cartao && <span>· {it._cartao}</span>}
                  {it.pagamento && <span>· {it.pagamento}</span>}
                  {it.data      && <span>· {fmtDate(it.data)}</span>}
                  {it.pago !== undefined && (
                    <span className={it.pago ? "text-emerald-400" : "text-yellow-400"}>
                      · {it.pago ? "✅" : "⏳"}
                    </span>
                  )}
                </div>
              </div>
              <div className="shrink-0 text-sm font-semibold tabular-nums text-white">{brl(it.valor)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Pie custom tooltip ─────────────────────────────────── */
function PieTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 shadow-xl text-sm">
      <div className="flex items-center gap-2 mb-1">
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: d.payload.color }} />
        <span className="font-semibold text-white">{d.payload.name}</span>
      </div>
      <div className="text-white font-bold tabular-nums">{brl(d.value)}</div>
      <div className="text-slate-400 text-xs">{pct}% do total</div>
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────── */
export default function DashboardAnual() {
  const { data } = useAppData();
  const allCats = getCategories(data.customCategories);
  const year    = (data.selectedMonth || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`).slice(0, 4);

  const [filterTipo,  setFilterTipo]  = useState("all");
  const [detailMonth, setDetailMonth] = useState(null);

  /* ── Build allTx based on filter ── */
  const allTx = useMemo(() => {
    const items = [];
    if (filterTipo === "all" || filterTipo === "fixo")
      items.push(...(data.gastosFixos    || []).map((x) => ({ ...x, _tipo: "fixo" })));
    if (filterTipo === "all" || filterTipo === "variavel")
      items.push(...(data.gastosVariaveis|| []).map((x) => ({ ...x, _tipo: "variavel" })));
    if (filterTipo === "all" || filterTipo === "cartao")
      items.push(...(data.cartoes || []).flatMap((c) =>
        c.transacoes.map((t) => ({ ...t, _tipo: "cartao" }))
      ));
    return items;
  }, [data, filterTipo]);

  /* ── Aggregations ── */
  const { meses, fluxo, totalAno, totalEntradasAno, totalDiferencaAno, mediaMensal, somaCatAno } = useMemo(() => {
    const allEntradas = data.entradas || [];

    const mesesAgg = MES_CURTOS.map((mes, i) => {
      const ym = `${year}-${String(i + 1).padStart(2, "0")}`;
      const entradasMes = allEntradas.filter((e) => (e.data || "").startsWith(ym)).reduce((s, e) => s + Number(e.valor || 0), 0);
      const gastosMes   = allTx.filter((t) => (t.data || "").startsWith(ym)).reduce((s, t) => s + Number(t.valor || 0), 0);
      const pctGasto    = entradasMes > 0 ? (gastosMes / entradasMes) * 100 : 0;
      return { mes, ym, entradas: entradasMes, gastos: gastosMes, diferenca: entradasMes - gastosMes, pctGasto };
    });

    const fluxoAgg = MES_CURTOS.map((mes, i) => {
      const ym  = `${year}-${String(i + 1).padStart(2, "0")}`;
      const row = { mes, _hasData: allTx.some((t) => (t.data || "").startsWith(ym)) };
      allCats.forEach((c) => {
        row[c.id] = allTx.filter((t) => (t.data || "").startsWith(ym) && t.cat === c.id).reduce((s, t) => s + Number(t.valor || 0), 0);
      });
      return row;
    });

    const tAno        = mesesAgg.reduce((s, m) => s + m.gastos, 0);
    const tEntradas   = mesesAgg.reduce((s, m) => s + m.entradas, 0);
    const tDiff       = tEntradas - tAno;
    const mesesComDados = mesesAgg.filter((m) => m.gastos > 0).length;
    const media       = mesesComDados > 0 ? tAno / mesesComDados : 0;

    const somaCat = allCats
      .map((c) => ({
        key: c.id, name: c.name, color: c.color,
        total: allTx.filter((t) => (t.data || "").startsWith(year) && t.cat === c.id).reduce((s, t) => s + Number(t.valor || 0), 0),
      }))
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);

    return {
      meses:             mesesAgg,
      fluxo:             fluxoAgg,
      totalAno:          tAno,
      totalEntradasAno:  tEntradas,
      totalDiferencaAno: tDiff,
      mediaMensal:       media,
      somaCatAno:        somaCat,
    };
  }, [data, allTx, allCats, year]);

  const activeCatKeys = somaCatAno.map((c) => c.key);

  /* ── Custom X-axis tick (dim months without data) ── */
  const AreaXTick = useCallback(({ x, y, payload }) => {
    const idx  = MES_CURTOS.indexOf(payload.value);
    const empty = idx >= 0 && !fluxo[idx]?._hasData;
    return (
      <text x={x} y={y + 12} textAnchor="middle" fontSize={11} fill={empty ? "#475569" : "#94a3b8"} fontStyle={empty ? "italic" : "normal"}>
        {payload.value}
      </text>
    );
  }, [fluxo]);

  /* ── Export ── */
  const exportXLS = () => {
    const wb = XLSX.utils.book_new();
    // Resumo
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
      { Métrica: "Total Gasto no Ano",       "Valor (R$)": totalAno },
      { Métrica: "Total Entradas no Ano",    "Valor (R$)": totalEntradasAno },
      { Métrica: "Economia do Ano",          "Valor (R$)": totalDiferencaAno },
      { Métrica: "Média Mensal de Gastos",   "Valor (R$)": mediaMensal },
    ]), "Resumo");
    // Mensal
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      meses.map((m, i) => ({
        Mês:         MES_NOMES[i],
        Entradas:    m.entradas,
        Gastos:      m.gastos,
        "% Gasto":   m.entradas > 0 ? `${m.pctGasto.toFixed(1)}%` : "—",
        "Diferença": m.diferenca,
      }))
    ), "Mensal");
    // Por categoria
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      somaCatAno.map((c) => ({
        Categoria:      c.name,
        "Total (R$)":   c.total,
        "% do Total":   totalAno > 0 ? `${((c.total / totalAno) * 100).toFixed(1)}%` : "—",
      }))
    ), "Por Categoria");
    XLSX.writeFile(wb, `relatorio-anual-${year}.xlsx`);
    toast.success("Relatório exportado para Excel!");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Relatório Anual ${year}`, 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")}`, 14, 22);

    autoTable(doc, {
      head: [["Métrica", "Valor"]],
      body: [
        ["Total Gasto no Ano",     brl(totalAno)],
        ["Total Entradas no Ano",  brl(totalEntradasAno)],
        ["Economia do Ano",        brl(totalDiferencaAno)],
        ["Média Mensal de Gastos", brl(mediaMensal)],
      ],
      startY: 27,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [15, 23, 42], textColor: [148, 163, 184] },
      bodyStyles: { fillColor: [17, 24, 39], textColor: [226, 232, 240] },
      alternateRowStyles: { fillColor: [30, 41, 59] },
    });

    const y1 = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text("Resumo Mensal", 14, y1);

    autoTable(doc, {
      head: [["Mês", "Entradas", "Gastos", "% Gasto", "Diferença"]],
      body: meses.map((m, i) => [
        MES_NOMES[i],
        brl(m.entradas),
        brl(m.gastos),
        m.entradas > 0 ? `${m.pctGasto.toFixed(1)}%` : "—",
        brl(m.diferenca),
      ]),
      startY: y1 + 4,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [15, 23, 42], textColor: [148, 163, 184] },
      bodyStyles: { fillColor: [17, 24, 39], textColor: [226, 232, 240] },
      alternateRowStyles: { fillColor: [30, 41, 59] },
    });

    const y2 = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(11);
    doc.text("Gastos por Categoria", 14, y2);
    autoTable(doc, {
      head: [["Categoria", "Total", "% do Total"]],
      body: somaCatAno.map((c) => [
        c.name,
        brl(c.total),
        totalAno > 0 ? `${((c.total / totalAno) * 100).toFixed(1)}%` : "—",
      ]),
      startY: y2 + 4,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [15, 23, 42], textColor: [148, 163, 184] },
      bodyStyles: { fillColor: [17, 24, 39], textColor: [226, 232, 240] },
      alternateRowStyles: { fillColor: [30, 41, 59] },
    });

    doc.save(`relatorio-anual-${year}.pdf`);
    toast.success("Relatório exportado para PDF!");
  };

  /* ── Render ── */
  return (
    <AppLayout>
      <PageHeader title="Dashboard Financeiro" subtitle={`Visão Analítica Anual · ${year}`} testid="anual" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 space-y-6">

        {/* ── FILTER + EXPORT BAR ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 p-1 bg-slate-900 border border-slate-800 rounded-2xl flex-wrap">
            {[
              { id: "all",      label: "Todos" },
              { id: "fixo",     label: "Fixos" },
              { id: "variavel", label: "Variáveis" },
              { id: "cartao",   label: "Cartões" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilterTipo(f.id)}
                className={`h-8 px-4 rounded-xl text-sm font-medium transition ${
                  filterTipo === f.id ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <ExportMenu onXLS={exportXLS} onPDF={exportPDF} />
        </div>

        {/* ── 4 SUMMARY CARDS ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-2xl p-5 bg-gradient-to-br from-rose-600/80 to-rose-800/80 border border-rose-500/30" data-testid="total-ano">
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <BarChart3 className="h-4 w-4" />
              <div className="text-xs uppercase tracking-wider font-semibold">Total Gasto no Ano</div>
            </div>
            <div className="font-display text-2xl font-bold tabular-nums">{brl(totalAno)}</div>
            <div className="mt-1 text-xs opacity-70">Fixos + Variáveis + Cartões</div>
          </div>

          <div className="rounded-2xl p-5 bg-gradient-to-br from-blue-600/80 to-blue-800/80 border border-blue-500/30">
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <Wallet className="h-4 w-4" />
              <div className="text-xs uppercase tracking-wider font-semibold">Total Entradas</div>
            </div>
            <div className="font-display text-2xl font-bold tabular-nums">{brl(totalEntradasAno)}</div>
            <div className="mt-1 text-xs opacity-70">Soma de todas as entradas em {year}</div>
          </div>

          <div className={`rounded-2xl p-5 border ${
            totalDiferencaAno >= 0
              ? "bg-gradient-to-br from-emerald-600/80 to-emerald-800/80 border-emerald-500/30"
              : "bg-gradient-to-br from-rose-700/80 to-rose-900/80 border-rose-500/30"
          }`}>
            <div className="flex items-center gap-2 mb-2 opacity-90">
              {totalDiferencaAno >= 0
                ? <TrendingUp  className="h-4 w-4" />
                : <TrendingDown className="h-4 w-4" />}
              <div className="text-xs uppercase tracking-wider font-semibold">Economia do Ano</div>
            </div>
            <div className="font-display text-2xl font-bold tabular-nums">{brl(totalDiferencaAno)}</div>
            <div className="mt-1 text-xs opacity-70">
              {totalDiferencaAno >= 0 ? "Sobrou no ano todo" : "Deficit no ano todo"}
            </div>
          </div>

          <div className="rounded-2xl p-5 bg-gradient-to-br from-violet-600/80 to-violet-800/80 border border-violet-500/30">
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <Calculator className="h-4 w-4" />
              <div className="text-xs uppercase tracking-wider font-semibold">Média Mensal</div>
            </div>
            <div className="font-display text-2xl font-bold tabular-nums">{brl(mediaMensal)}</div>
            <div className="mt-1 text-xs opacity-70">
              Média nos meses com lançamentos
            </div>
          </div>
        </div>

        {/* ── CATEGORY TOTALS GRID ── */}
        {somaCatAno.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {somaCatAno.slice(0, 7).map((c) => (
              <div
                key={c.key}
                className="rounded-xl p-4 bg-slate-900 border border-slate-800"
                style={{ borderTop: `3px solid ${c.color}` }}
                data-testid={`cat-sum-${c.key}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold truncate">{c.name}</div>
                <div className="mt-1 text-sm font-display font-bold text-white tabular-nums">{brl(c.total)}</div>
                {totalAno > 0 && (
                  <div className="text-[10px] text-slate-500 mt-0.5">{((c.total / totalAno) * 100).toFixed(1)}%</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── PIE + AREA CHARTS ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

          {/* Pie chart */}
          {somaCatAno.length > 0 && (
            <div className="lg:col-span-2 rounded-2xl p-5 bg-slate-900 border border-slate-800" data-testid="pie-cat">
              <h3 className="font-display text-base font-bold mb-1">Distribuição por Categoria</h3>
              <p className="text-xs text-slate-400 mb-3">Proporção anual · {year}</p>
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={somaCatAno}
                      dataKey="total"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {somaCatAno.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip total={totalAno} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Custom legend */}
              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-28 overflow-y-auto">
                {somaCatAno.map((c) => (
                  <div key={c.key} className="flex items-center gap-1.5 text-[11px] text-slate-300 min-w-0">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: c.color }} />
                    <span className="truncate">{c.name}</span>
                    <span className="text-slate-500 shrink-0 ml-auto">
                      {totalAno > 0 ? `${((c.total / totalAno) * 100).toFixed(0)}%` : "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Area chart */}
          <div className={`${somaCatAno.length > 0 ? "lg:col-span-3" : "lg:col-span-5"} rounded-2xl p-5 bg-slate-900 border border-slate-800`} data-testid="area-fluxo">
            <h3 className="font-display text-base font-bold">Fluxo por Categoria</h3>
            <p className="text-xs text-slate-400 mb-3">Gastos mensais empilhados · {year} {filterTipo !== "all" ? `· ${filterTipo === "fixo" ? "Fixos" : filterTipo === "variavel" ? "Variáveis" : "Cartões"}` : ""}</p>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={fluxo}>
                  <defs>
                    {activeCatKeys.map((k) => {
                      const color = allCats.find((c) => c.id === k)?.color || "#888";
                      return (
                        <linearGradient key={k} id={`g-${k}`} x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%"   stopColor={color} stopOpacity="0.7" />
                          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={AreaXTick} axisLine={false} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} width={34} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 10, color: "#fff", fontSize: 12 }} formatter={(v) => brl(v)} />
                  <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
                  {activeCatKeys.map((k) => {
                    const color = allCats.find((c) => c.id === k)?.color || "#888";
                    const name  = allCats.find((c) => c.id === k)?.name  || k;
                    return <Area key={k} type="monotone" dataKey={k} name={name} stackId="1" stroke={color} fill={`url(#g-${k})`} />;
                  })}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ── BAR CHART ── */}
        <div className="rounded-2xl p-5 bg-slate-900 border border-slate-800" data-testid="bar-resumo">
          <h3 className="font-display text-base font-bold">Entradas vs. Gastos</h3>
          <p className="text-xs text-slate-400 mb-3">Comparativo mensal · {year}</p>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={meses} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="mes" stroke="#64748b" fontSize={11} axisLine={false} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} width={34} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 10, color: "#fff", fontSize: 12 }} formatter={(v) => brl(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="entradas"  name="Entradas"  fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="gastos"    name="Gastos"    fill="#f43f5e" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="diferenca" name="Diferença" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── MONTHLY TABLE ── */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden" data-testid="tabela-mensal">
          {/* Header desktop */}
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-800">
            <div className="col-span-3">Mês</div>
            <div className="col-span-2 text-right">Entradas</div>
            <div className="col-span-2 text-right">Gastos</div>
            <div className="col-span-2 text-center">% Gasto</div>
            <div className="col-span-2 text-right">Diferença</div>
            <div className="col-span-1 text-right">Detalhe</div>
          </div>

          <div className="divide-y divide-slate-800">
            {meses.map((m, i) => {
              const pctColor  = getPctColor(m.pctGasto);
              const hasData   = m.gastos > 0 || m.entradas > 0;
              return (
                <div
                  key={m.mes}
                  onClick={() => hasData && setDetailMonth(m.ym)}
                  className={`hover:bg-slate-800/40 transition ${hasData ? "cursor-pointer" : "opacity-40"}`}
                  data-testid={`row-mes-${m.mes}`}
                >
                  {/* Desktop row */}
                  <div className="hidden md:grid grid-cols-12 px-5 py-3 items-center text-sm gap-1">
                    <div className="col-span-3 text-slate-200 font-medium" translate="no">
                      {MES_NOMES[i]}
                      <span className="text-slate-500 text-xs ml-1">/{year}</span>
                    </div>
                    <div className="col-span-2 text-right text-blue-300 tabular-nums text-xs">{m.entradas > 0 ? brl(m.entradas) : "—"}</div>
                    <div className="col-span-2 text-right text-rose-300 tabular-nums text-xs">{m.gastos > 0 ? brl(m.gastos) : "—"}</div>
                    <div className="col-span-2 text-center">
                      {m.entradas > 0 && m.gastos > 0 ? (
                        <span className={`text-xs font-bold ${pctColor}`}>{m.pctGasto.toFixed(0)}% da renda</span>
                      ) : <span className="text-xs text-slate-600">—</span>}
                    </div>
                    <div className={`col-span-2 text-right font-semibold tabular-nums text-xs ${m.diferenca >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {hasData ? brl(m.diferenca) : "—"}
                    </div>
                    <div className="col-span-1 text-right">
                      {hasData && <span className="text-slate-500 text-xs hover:text-emerald-300 transition">→</span>}
                    </div>
                  </div>

                  {/* Mobile row */}
                  <div className="md:hidden px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate" translate="no">{MES_NOMES[i]}</div>
                        {hasData && m.entradas > 0 && m.gastos > 0 && (
                          <div className={`text-xs font-semibold mt-0.5 ${pctColor}`}>{m.pctGasto.toFixed(0)}% da renda</div>
                        )}
                      </div>
                      <div className="text-right shrink-0 space-y-0.5">
                        {m.entradas > 0 && <div className="text-xs text-blue-300 tabular-nums">{brl(m.entradas)}</div>}
                        {m.gastos > 0 && <div className="text-xs text-rose-300 tabular-nums">{brl(m.gastos)}</div>}
                        {hasData && (
                          <div className={`text-xs font-bold tabular-nums ${m.diferenca >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                            {brl(m.diferenca)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Annual totals row — desktop */}
            <div className="hidden md:block bg-slate-950/60 border-t-2 border-emerald-500/30" data-testid="row-totais-ano">
              <div className="grid grid-cols-12 px-5 py-4 items-center text-sm gap-1">
                <div className="col-span-3 font-display font-bold uppercase tracking-wider text-[10px] text-emerald-300">
                  Total {year}
                </div>
                <div className="col-span-2 text-right text-blue-300 font-bold tabular-nums text-xs">{brl(totalEntradasAno)}</div>
                <div className="col-span-2 text-right text-rose-300 font-bold tabular-nums text-xs">{brl(totalAno)}</div>
                <div className="col-span-2 text-center">
                  {totalEntradasAno > 0 && (
                    <span className={`text-xs font-bold ${getPctColor((totalAno / totalEntradasAno) * 100)}`}>
                      {((totalAno / totalEntradasAno) * 100).toFixed(0)}% da renda
                    </span>
                  )}
                </div>
                <div className={`col-span-3 text-right font-bold tabular-nums text-xs ${totalDiferencaAno >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {brl(totalDiferencaAno)}
                </div>
              </div>
            </div>

            {/* Annual totals row — mobile */}
            <div className="md:hidden px-4 py-4 bg-slate-950/60 border-t-2 border-emerald-500/30" data-testid="row-totais-ano-mobile">
              <div className="flex items-center justify-between mb-3">
                <span className="font-display font-bold uppercase tracking-wider text-[11px] text-emerald-300">Total {year}</span>
                {totalEntradasAno > 0 && (
                  <span className={`text-xs font-bold ${getPctColor((totalAno / totalEntradasAno) * 100)}`}>
                    {((totalAno / totalEntradasAno) * 100).toFixed(0)}% da renda
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Entradas</div>
                  <div className="text-xs font-bold text-blue-300 tabular-nums">{brl(totalEntradasAno)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Gastos</div>
                  <div className="text-xs font-bold text-rose-300 tabular-nums">{brl(totalAno)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Diferença</div>
                  <div className={`text-xs font-bold tabular-nums ${totalDiferencaAno >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{brl(totalDiferencaAno)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── MONTH DETAIL MODAL ── */}
      {detailMonth && (
        <MonthDetailModal
          ymPrefix={detailMonth}
          data={data}
          onClose={() => setDetailMonth(null)}
        />
      )}
    </AppLayout>
  );
}
